import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import HomePage from './components/HomePage';
import LoginPage from './pages/LoginPage';
import BookingPanel from './pages/BookingPanel';
import AdminPanelPrepaid from './pages/AdminPanelPrepaid';
import AdminPanelPostpaid from './pages/AdminPanelPostpaid';
import BookingsPanel from './pages/BookingsPanel';
import AdminPanelUserAssign from './pages/AdminPanelUserAssign';
import DiscountManagement from './pages/DiscountManagement';
// import SettlementAccounts from './pages/SettlementAccounts';
import HotelAccounts from './pages/HotelAccounts';
import PaymentSettlements from './pages/PaymentSettlements';
import HotelBankDetails from './pages/HotelBankDetails';
import FinanceReport from './pages/FinanceReport';
import PromoCodes from './pages/PromoCodes';
import GoogleStatus from './pages/GoogleStatus';
import MappingData from './pages/MappingData';
import SupportBookingLookup from './pages/SupportBookingLookup';
import RoomTypeApprovals from './pages/RoomTypeApprovals';
import GstStatus from './pages/GstStatus';
import TransferBooking from './pages/TransferBooking';
import SfOnboardApprovals from './pages/SfOnboardApprovals';
import StayflexiHotelAddressData from './pages/StayflexiHotelAddressData';
import AdminUsers from './pages/AdminUsers';
import IvrPanel from './pages/IvrPanel';
import { FEATURES } from './utils/permissions';

// Create a custom theme with the TabTrips color scheme
const theme = createTheme({
  palette: {
    primary: {
      main: '#FF7700',
      light: '#FF9933',
      dark: '#E65100',
    },
    secondary: {
      main: '#2E4057',
      light: '#4A5C6A',
      dark: '#1A252F',
    },
    background: {
      default: '#f8f9fa',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: { fontWeight: 700 },
    h2: { fontWeight: 700 },
    h3: { fontWeight: 700 },
    h4: { fontWeight: 600 },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
  },
  components: {
    MuiButton: { styleOverrides: { root: { borderRadius: 8, textTransform: 'none', fontWeight: 600 } } },
    MuiCard: { styleOverrides: { root: { borderRadius: 12 } } },
    MuiPaper: { styleOverrides: { root: { borderRadius: 12 } } },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/booking" element={
              <ProtectedRoute>
                <BookingPanel />
              </ProtectedRoute>
            } />
            <Route path="/bookings" element={
              <ProtectedRoute>
                <BookingsPanel />
              </ProtectedRoute>
            } />
            <Route path="/prepaid" element={
              <ProtectedRoute>
                <AdminPanelPrepaid />
              </ProtectedRoute>
            } />
            <Route path="/postpaid" element={
              <ProtectedRoute>
                <AdminPanelPostpaid />
              </ProtectedRoute>
            } />
            <Route path="/user-assign" element={
              <ProtectedRoute>
                <AdminPanelUserAssign />
              </ProtectedRoute>
            } />
            <Route path="/discounts" element={
              <ProtectedRoute>
                <DiscountManagement />
              </ProtectedRoute>
            } />
            {/* Settlement Accounts temporarily disabled */}
            {/* <Route path="/settlement-accounts" element={
              <ProtectedRoute>
                <SettlementAccounts />
              </ProtectedRoute>
            } /> */}
            <Route path="/hotel-accounts" element={
              <ProtectedRoute>
                <HotelAccounts />
              </ProtectedRoute>
            } />
            <Route path="/payment-settlements" element={
              <ProtectedRoute>
                <PaymentSettlements />
              </ProtectedRoute>
            } />
            <Route path="/hotel-bank-details" element={
              <ProtectedRoute>
                <HotelBankDetails />
              </ProtectedRoute>
            } />
            <Route path="/finance" element={
              <ProtectedRoute>
                <FinanceReport />
              </ProtectedRoute>
            } />
            <Route path="/promo-codes" element={
              <ProtectedRoute>
                <PromoCodes />
              </ProtectedRoute>
            } />
            <Route path="/google-status" element={
              <ProtectedRoute>
                <GoogleStatus />
              </ProtectedRoute>
            } />
            <Route path="/mapping-data" element={
              <ProtectedRoute>
                <MappingData />
              </ProtectedRoute>
            } />
            <Route path="/booking-details" element={
              <ProtectedRoute>
                <SupportBookingLookup />
              </ProtectedRoute>
            } />
            <Route path="/room-type-approvals" element={
              <ProtectedRoute>
                <RoomTypeApprovals />
              </ProtectedRoute>
            } />
            <Route path="/gst-status" element={
              <ProtectedRoute>
                <GstStatus />
              </ProtectedRoute>
            } />
            <Route path="/transfer-booking" element={
              <ProtectedRoute>
                <TransferBooking />
              </ProtectedRoute>
            } />
            <Route path="/sf-onboarding" element={
              <ProtectedRoute>
                <SfOnboardApprovals />
              </ProtectedRoute>
            } />
            <Route path="/gha-excel" element={
              <ProtectedRoute>
                <StayflexiHotelAddressData />
              </ProtectedRoute>
            } />
            <Route path="/admin-users" element={
              <ProtectedRoute feature={FEATURES.manage_admin_users}>
                <AdminUsers />
              </ProtectedRoute>
            } />
            <Route path="/ivr" element={
              <ProtectedRoute feature={FEATURES.ivr_admin}>
                <IvrPanel />
              </ProtectedRoute>
            } />
            {/* Catch-all route for any unmatched paths */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
